// Visitor Tracking Service for Grey Coffee
// Tracks visitor data including device type, page views, and stores in Firebase

import { db } from './firebase';
import { collection, addDoc, serverTimestamp, query, where, getDocs, orderBy, limit } from 'firebase/firestore';

class VisitorTrackingService {
  constructor() {
    this.sessionId = this.generateSessionId();
    this.deviceInfo = this.getDeviceInfo();
    this.visitorId = this.getOrCreateVisitorId();
    this.sessionStartTime = Date.now();
    this.hasTrackedVisit = false;
    
    // Track initial visit after consent is loaded
    this.init();
  }

  init() {
    // Wait for cookie consent before tracking
    const checkConsent = () => {
      const consent = localStorage.getItem('grey-cookie-consent');
      if (consent) {
        const consentData = JSON.parse(consent);
        if (consentData.analytics) {
          this.trackVisit();
        }
      }
    };

    // Check immediately and then monitor changes
    checkConsent();
    
    // Also listen for consent changes
    window.addEventListener('storage', (e) => {
      if (e.key === 'grey-cookie-consent') {
        checkConsent();
      }
    });
  }

  generateSessionId() {
    return 'sess_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  getOrCreateVisitorId() {
    let visitorId = localStorage.getItem('grey-visitor-id');
    if (!visitorId) {
      visitorId = 'vis_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('grey-visitor-id', visitorId);
      localStorage.setItem('grey-visitor-created', new Date().toISOString());
    }
    return visitorId;
  }

  getDeviceInfo() {
    const userAgent = navigator.userAgent;
    const isMobile = /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    const isTablet = /iPad|Android(?!.*Mobile)/i.test(userAgent);
    
    let deviceType = 'Desktop';
    if (isMobile && !isTablet) {
      deviceType = 'Mobile';
    } else if (isTablet) {
      deviceType = 'Tablet';
    }

    return {
      type: deviceType,
      userAgent: userAgent,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      language: navigator.language,
      platform: navigator.platform,
      cookiesEnabled: navigator.cookieEnabled,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
  }

  async trackVisit() {
    if (this.hasTrackedVisit) return;
    
    try {
      const visitData = {
        visitorId: this.visitorId,
        sessionId: this.sessionId,
        deviceType: this.deviceInfo.type,
        deviceInfo: this.deviceInfo,
        timestamp: serverTimestamp(),
        page: window.location.pathname,
        referrer: document.referrer || null,
        isNewVisitor: !localStorage.getItem('grey-visitor-returning'),
        userAgent: navigator.userAgent,
        ip: null // Will be populated by Firebase functions if needed
      };

      await addDoc(collection(db, 'visitor_tracking'), visitData);
      
      // Mark as returning visitor
      localStorage.setItem('grey-visitor-returning', 'true');
      this.hasTrackedVisit = true;
      
      console.log('Visitor tracked:', this.deviceInfo.type);
    } catch (error) {
      console.error('Error tracking visitor:', error);
    }
  }

  async trackPageView(path = window.location.pathname) {
    const consent = localStorage.getItem('grey-cookie-consent');
    if (!consent) return;
    
    const consentData = JSON.parse(consent);
    if (!consentData.analytics) return;

    try {
      const pageViewData = {
        visitorId: this.visitorId,
        sessionId: this.sessionId,
        page: path,
        title: document.title,
        deviceType: this.deviceInfo.type,
        timestamp: serverTimestamp(),
        referrer: document.referrer || null,
        timeOnPreviousPage: null // Could be calculated
      };

      await addDoc(collection(db, 'page_views'), pageViewData);
    } catch (error) {
      console.error('Error tracking page view:', error);
    }
  }

  async trackEvent(category, action, label = null, value = null) {
    const consent = localStorage.getItem('grey-cookie-consent');
    if (!consent) return;
    
    const consentData = JSON.parse(consent);
    if (!consentData.analytics) return;

    try {
      const eventData = {
        visitorId: this.visitorId,
        sessionId: this.sessionId,
        category,
        action,
        label,
        value,
        deviceType: this.deviceInfo.type,
        page: window.location.pathname,
        timestamp: serverTimestamp()
      };

      await addDoc(collection(db, 'analytics_events'), eventData);
    } catch (error) {
      console.error('Error tracking event:', error);
    }
  }

  // Admin dashboard data retrieval methods
  static async getVisitorStats(timeframe = 7) {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - timeframe);

      // Get visitor data
      const visitorsQuery = query(
        collection(db, 'visitor_tracking'),
        where('timestamp', '>=', startDate),
        orderBy('timestamp', 'desc')
      );

      const visitorsSnapshot = await getDocs(visitorsQuery);
      const visitors = visitorsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate() || new Date()
      }));

      // Calculate stats
      const stats = {
        totalVisitors: visitors.length,
        uniqueVisitors: new Set(visitors.map(v => v.visitorId)).size,
        mobileVisitors: visitors.filter(v => v.deviceType === 'Mobile').length,
        desktopVisitors: visitors.filter(v => v.deviceType === 'Desktop').length,
        tabletVisitors: visitors.filter(v => v.deviceType === 'Tablet').length,
        newVisitors: visitors.filter(v => v.isNewVisitor).length,
        returningVisitors: visitors.filter(v => !v.isNewVisitor).length,
        dailyBreakdown: {},
        hourlyBreakdown: new Array(24).fill(0),
        topPages: {},
        topReferrers: {}
      };

      // Calculate daily breakdown
      visitors.forEach(visitor => {
        const date = visitor.timestamp.toDateString();
        if (!stats.dailyBreakdown[date]) {
          stats.dailyBreakdown[date] = {
            total: 0,
            mobile: 0,
            desktop: 0,
            tablet: 0,
            new: 0,
            returning: 0
          };
        }
        
        stats.dailyBreakdown[date].total++;
        stats.dailyBreakdown[date][visitor.deviceType.toLowerCase()]++;
        stats.dailyBreakdown[date][visitor.isNewVisitor ? 'new' : 'returning']++;
        
        // Hourly breakdown
        const hour = visitor.timestamp.getHours();
        stats.hourlyBreakdown[hour]++;
      });

      // Top pages and referrers
      visitors.forEach(visitor => {
        if (visitor.page) {
          stats.topPages[visitor.page] = (stats.topPages[visitor.page] || 0) + 1;
        }
        if (visitor.referrer) {
          stats.topReferrers[visitor.referrer] = (stats.topReferrers[visitor.referrer] || 0) + 1;
        }
      });

      return stats;
    } catch (error) {
      console.error('Error fetching visitor stats:', error);
      return null;
    }
  }

  static async getRealtimeVisitors() {
    try {
      const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
      
      const recentQuery = query(
        collection(db, 'visitor_tracking'),
        where('timestamp', '>=', fiveMinutesAgo),
        orderBy('timestamp', 'desc')
      );

      const snapshot = await getDocs(recentQuery);
      const recentVisitors = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate() || new Date()
      }));

      return {
        total: new Set(recentVisitors.map(v => v.sessionId)).size,
        mobile: recentVisitors.filter(v => v.deviceType === 'Mobile').length,
        desktop: recentVisitors.filter(v => v.deviceType === 'Desktop').length,
        tablet: recentVisitors.filter(v => v.deviceType === 'Tablet').length
      };
    } catch (error) {
      console.error('Error fetching realtime visitors:', error);
      return { total: 0, mobile: 0, desktop: 0, tablet: 0 };
    }
  }

  static async getTopDevices(timeframe = 7) {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - timeframe);

      const visitorsQuery = query(
        collection(db, 'visitor_tracking'),
        where('timestamp', '>=', startDate)
      );

      const snapshot = await getDocs(visitorsQuery);
      const devices = {};
      
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const deviceKey = `${data.deviceType} - ${data.deviceInfo?.platform || 'Unknown'}`;
        devices[deviceKey] = (devices[deviceKey] || 0) + 1;
      });

      return Object.entries(devices)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10)
        .map(([device, count]) => ({ device, count }));
    } catch (error) {
      console.error('Error fetching top devices:', error);
      return [];
    }
  }
}

// Create singleton instance
const visitorTracking = new VisitorTrackingService();

export default visitorTracking;
export { VisitorTrackingService };